/**
 * Authenticated-session storage for ZAFROI Kosh.
 *
 * Mirrors the erp-app / engage-app pattern:
 *
 * - Refresh token + cached user persist in localStorage so a tab refresh
 *   doesn't kick the user back to /login.
 * - Access token lives in memory only (limits XSS exposure).
 * - On boot, FE has a refresh token but no access token; the api-client
 *   transparently calls /auth/refresh to mint a new access token before
 *   the first protected request.
 *
 * Storage key is namespaced under 'zafroi.kosh.session.v1' (distinct from
 * other apps even though they're on different subdomains — keeps cross-app
 * coexistence trivial if we ever serve both on the same origin).
 */

import type { CurrentUser } from '@/types/auth';

const STORAGE_KEY = 'zafroi.kosh.session.v1';

interface PersistedSession {
  refreshToken: string;
  expiresAt: string;
  user: CurrentUser;
}

interface ActiveSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  // user is nullable because the SSO cookie-only boot path lands here
  // with tokens before /auth/me has hydrated the user. AuthProvider
  // fills this in synchronously after refresh resolves.
  user: CurrentUser | null;
}

let active: ActiveSession | null = null;
const listeners = new Set<(session: ActiveSession | null) => void>();

function notify(): void {
  listeners.forEach((fn) => fn(active));
}

function readPersisted(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedSession;
  } catch {
    return null;
  }
}

function writePersisted(p: PersistedSession | null): void {
  try {
    if (p) localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable (private browsing, full disk) — non-fatal.
  }
}

export const session = {
  /** True if a refresh token is on disk. We may not have an access token in memory yet. */
  hasPersistedSession(): boolean {
    return readPersisted() !== null;
  },

  /** Cached user (no API call). Lets the topbar render at boot before /auth/refresh resolves. */
  cachedUser(): CurrentUser | null {
    return readPersisted()?.user ?? null;
  },

  /** In-memory active session, or null. */
  current(): ActiveSession | null {
    return active;
  },

  /** Refresh token from disk — used by the api-client interceptor on 401. */
  getRefreshToken(): string | null {
    return readPersisted()?.refreshToken ?? null;
  },

  /** Set a fresh session after a successful login. */
  set(args: {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
    user: CurrentUser;
  }): void {
    active = { ...args };
    writePersisted({
      refreshToken: args.refreshToken,
      expiresAt: args.expiresAt,
      user: args.user,
    });
    notify();
  },

  /**
   * Update tokens after a /auth/refresh.
   *
   * Three code paths land here:
   *   - We had localStorage AND in-memory active session → just rotate tokens.
   *   - We had localStorage (cached user) but no in-memory session yet (boot
   *     after tab restore) → rehydrate active from cached user.
   *   - We had NEITHER localStorage NOR in-memory session — the cookie-only
   *     SSO path. Initialize active with user=null; AuthProvider follows up
   *     with /auth/me + session.setUser() to populate it.
   *
   * Persistence to localStorage only happens once we have a user object
   * (otherwise we'd write a half-formed PersistedSession that breaks
   * cachedUser()).
   */
  updateAccessToken(
    accessToken: string,
    refreshToken: string,
    expiresAt: string,
  ): void {
    if (!active) {
      const cached = readPersisted();
      active = {
        accessToken,
        refreshToken,
        expiresAt,
        user: cached?.user ?? null,
      };
    } else {
      active = { ...active, accessToken, refreshToken, expiresAt };
    }
    if (active.user) {
      writePersisted({
        refreshToken,
        expiresAt,
        user: active.user,
      });
    }
    notify();
  },

  /** Replace the cached user (after a /auth/me call). */
  setUser(user: CurrentUser): void {
    if (active) {
      active = { ...active, user };
      // Persist using the in-memory active session — this is the path
      // SSO bootstrap takes, where readPersisted() was null because
      // updateAccessToken hadn't seeded localStorage yet (we deferred
      // until we had a real user).
      writePersisted({
        refreshToken: active.refreshToken,
        expiresAt: active.expiresAt,
        user,
      });
    } else {
      // Defensive: setUser called without an active session — keep any
      // existing PersistedSession but update its user field.
      const persisted = readPersisted();
      if (persisted) {
        writePersisted({ ...persisted, user });
      }
    }
    notify();
  },

  /** Clear everything — log out / refresh failed. */
  clear(): void {
    active = null;
    writePersisted(null);
    notify();
  },

  /** Subscribe to session changes — used by AuthProvider. */
  subscribe(fn: (session: ActiveSession | null) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

export type { ActiveSession, PersistedSession };
